import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  TextField, Button, Chip, IconButton, Collapse, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, ListItemText, Tooltip,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import dayjs from 'dayjs';
import PaymentIcon from '@mui/icons-material/Payment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StoreIcon from '@mui/icons-material/Store';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DashboardSidebar from '../components/DashboardSidebar';

import { SYS_API_BASE } from '../constants/calendar';

interface LineItem {
  name: string;
  quantity: string;
  base_price: number;
  total: number;
  currency: string;
  note: string;
  uid: string;
}

interface PaymentRecord {
  id: number;
  payment_id: string;
  order_id: string | null;
  payment_status: string | null;
  source_type: string | null;
  amount: number | null;
  currency: string;
  total_amount: number | null;
  refunded_amount: number | null;
  card_brand: string | null;
  card_last4: string | null;
  entry_method: string | null;
  device_id: string | null;
  device_name: string | null;
  buyer_email: string | null;
  payment_note: string | null;
  receipt_url: string | null;
  payment_created_at: string | null;
  // Order fields
  order_state: string | null;
  line_items: LineItem[] | null;
  order_total: number | null;
  order_source: string | null;
  // Invoice fields
  invoice_id: string | null;
  invoice_number: string | null;
  invoice_title: string | null;
  invoice_status: string | null;
  delivery_method: string | null;
  computed_amount: number | null;
  total_completed_amount: number | null;
  sale_or_service_date: string | null;
  contact_code: string | null;
  customer_email: string | null;
  customer_family_name: string | null;
  customer_given_name: string | null;
  customer_phone: string | null;
}

interface MonthlySummary {
  payment: { total: number; count: number };
  sales: { total_paid: number; total_invoiced: number; invoice_count: number; payment_count: number; [key: string]: any };
  refunds: { total: number; count: number };
  cash_drawer: { sales: number; paid_in: number; paid_out: number; refunds: number; shift_count: number };
}

const STATUS_COLORS: Record<string, string> = {
  PAID: '#10b981',
  COMPLETED: '#10b981',
  UNPAID: '#f59e0b',
  OPEN: '#f59e0b',
  PARTIALLY_PAID: '#f59e0b',
  DRAFT: '#94a3b8',
  CANCELLED: '#ef4444',
  CANCELED: '#ef4444',
};

const ALL_STATUSES = [
  'COMPLETED', 'PAID', 'UNPAID', 'OPEN', 'PARTIALLY_PAID', 'DRAFT', 'CANCELLED',
] as const;

function HeaderWithHelp({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
      {label}
      <Tooltip title={tooltip} arrow placement="top">
        <HelpOutlineIcon sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
}

function formatYen(amount: number | null): string {
  if (amount == null) return '-';
  return `¥${amount.toLocaleString()}`;
}

function LineItemsTable({ items }: { items: LineItem[] }) {
  return (
    <Table size="small" sx={{ bgcolor: '#f8fafc' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>商品名</TableCell>
          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>数量</TableCell>
          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>単価</TableCell>
          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>合計</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, idx) => (
          <TableRow key={idx}>
            <TableCell sx={{ fontSize: '0.8rem' }}>{item.name}</TableCell>
            <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{item.quantity}</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatYen(item.base_price)}</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatYen(item.total)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function PaymentCheck() {
  const sysApiBase = SYS_API_BASE;

  // フィルタ
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [statusFilter, setStatusFilter] = useState<string[]>(['PAID', 'COMPLETED']);
  const [contactCodeFilter, setContactCodeFilter] = useState('');

  // セクション折り畳み
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({
    monthly: true,
    payments: true,
    cash: false,
  });
  const toggleSection = (key: string) => setSectionOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  // データ
  const [records, setRecords] = useState<PaymentRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 取得状態
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState<any>(null);

  // ドロワーイベント
  const [cashEvents, setCashEvents] = useState<any[]>([]);
  const [cashSummary, setCashSummary] = useState<any>(null);
  const [cashLoading, setCashLoading] = useState(false);

  // 取得モーダル
  const [fetchModalOpen, setFetchModalOpen] = useState(false);
  const [fetchStartDate, setFetchStartDate] = useState(dayjs().subtract(3, 'day').format('YYYY-MM-DD'));
  const [fetchEndDate, setFetchEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [locationIndex, setLocationIndex] = useState(0);
  const [resyncInvoices, setResyncInvoices] = useState(false);
  const [fetchApis, setFetchApis] = useState<Record<string, boolean>>({
    payments: true,
    orders: true,
    invoices: true,
    customers: false,
    refunds: true,
    cash_drawers: true,
  });
  const toggleFetchApi = (key: string) => setFetchApis((prev) => ({ ...prev, [key]: !prev[key] }));

  // 展開行
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // 月間サマリー
  // summaryMonth/summary は月別推移テーブルに統合済み

  // 月別一覧
  interface MonthRow {
    month: string;
    is_current: boolean;
    payment: { total: number; count: number };
    sales: { total_paid: number; total_invoiced: number; count: number };
    refunds: { total: number; count: number };
    cash: { total: number; count: number };
    advance?: { total: number; count: number };
    advance_carry?: { total: number; count: number };
  }
  const [yearlyData, setYearlyData] = useState<MonthRow[]>([]);
  const [yearlyLoading, setYearlyLoading] = useState(false);

  // 月別一覧をDBから取得
  const loadYearly = useCallback(async () => {
    setYearlyLoading(true);
    try {
      const res = await fetch(`${sysApiBase}/payment/summary_yearly.php`);
      const data = await res.json();
      if (data.status === 'ok') {
        setYearlyData(data.months || []);
      }
    } catch {
      // エラー無視
    } finally {
      setYearlyLoading(false);
    }
  }, [sysApiBase]);

  // ドロワーイベント取得
  const loadCashEvents = useCallback(async () => {
    setCashLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const res = await fetch(`${sysApiBase}/payment/list_cash_events.php?${params}`);
      const data = await res.json();
      if (data.status === 'ok') {
        setCashEvents(data.events || []);
        setCashSummary(data.summary || null);
      }
    } catch {
      // エラー無視
    } finally {
      setCashLoading(false);
    }
  }, [sysApiBase, startDate, endDate]);

  // ステータス全選択/解除
  const isAllStatusSelected = statusFilter.length === ALL_STATUSES.length;
  const handleStatusChange = (event: any) => {
    const val = event.target.value as string[];
    if (val.includes('__ALL__')) {
      // 全選択トグル
      setStatusFilter(isAllStatusSelected ? [] : [...ALL_STATUSES]);
    } else {
      setStatusFilter(val.filter((v: string) => v !== '__ALL__'));
    }
  };

  // データ一覧読み込み
  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        limit: '500',
      });
      if (statusFilter.length > 0 && statusFilter.length < ALL_STATUSES.length) {
        params.set('status', statusFilter.join(','));
      }
      if (contactCodeFilter) params.set('contact_code', contactCodeFilter);

      const res = await fetch(`${sysApiBase}/payment/list_v2.php?${params}`);
      const data = await res.json();
      if (data.status === 'ok') {
        setRecords(data.records || []);
        setTotal(data.total || 0);
      } else {
        setError(data.error || '取得に失敗しました');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sysApiBase, startDate, endDate, statusFilter, contactCodeFilter]);

  useEffect(() => {
    document.title = '決済確認 | レンタカー予約カレンダー';
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    loadYearly();
  }, [loadYearly]);

  useEffect(() => {
    loadCashEvents();
  }, [loadCashEvents]);

  // Square APIからデータ取得（5つのAPIを並列実行）
  const handleFetchFromSquare = async () => {
    setFetching(true);
    setFetchResult(null);
    setError('');

    const baseBody = {
      start_date: fetchStartDate,
      end_date: fetchEndDate,
      location_index: locationIndex,
    };

    // チェックされたAPIのみ実行
    const allEndpoints: Record<string, { name: string; url: string; body: any }> = {
      payments: { name: 'payments', url: `${sysApiBase}/payment/fetch_payments.php`, body: baseBody },
      orders: { name: 'orders', url: `${sysApiBase}/payment/fetch_orders_v2.php`, body: baseBody },
      invoices: { name: 'invoices', url: `${sysApiBase}/payment/fetch_invoices.php`, body: { ...baseBody, resync: resyncInvoices } },
      customers: { name: 'customers', url: `${sysApiBase}/payment/fetch_customers.php`, body: baseBody },
      refunds: { name: 'refunds', url: `${sysApiBase}/payment/fetch_refunds.php`, body: baseBody },
      cash_drawers: { name: 'cash_drawers', url: `${sysApiBase}/payment/fetch_cash_drawers.php`, body: baseBody },
    };
    const endpoints = Object.entries(allEndpoints)
      .filter(([key]) => fetchApis[key])
      .map(([, ep]) => ep);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      const results = await Promise.allSettled(
        endpoints.map(async (ep) => {
          const res = await fetch(ep.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(ep.body),
            signal: controller.signal,
          });
          const contentType = res.headers.get('content-type') || '';
          if (!res.ok) {
            if (res.status === 504 || res.status === 502) {
              throw new Error(`${ep.name}: タイムアウト`);
            }
            if (!contentType.includes('application/json')) {
              throw new Error(`${ep.name}: サーバーエラー (${res.status})`);
            }
          }
          const data = await res.json();
          return { name: ep.name, ...data };
        }),
      );
      clearTimeout(timeoutId);

      const summary: Record<string, any> = {};
      const errors: string[] = [];
      for (const r of results) {
        if (r.status === 'fulfilled') {
          summary[r.value.name] = r.value;
        } else {
          errors.push(r.reason?.message || '不明なエラー');
        }
      }

      setFetchResult({ results: summary, errors });
      if (errors.length > 0) {
        setError(`一部取得に失敗: ${errors.join(', ')}`);
      }

      setFetchModalOpen(false);
      await loadRecords();
      await loadYearly();
      await loadCashEvents();
    } catch (err: any) {
      if (err.name === 'AbortError') {
        setError('タイムアウトしました。期間を短くしてください。');
      } else {
        setError(err.message);
      }
    } finally {
      setFetching(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <DashboardSidebar />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 }, overflow: 'auto' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon /> 決済確認
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Square決済データの取得・確認
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            onClick={() => setFetchModalOpen(true)}
            sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, whiteSpace: 'nowrap' }}
          >
            データを取得
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {fetchResult && (
          <Alert severity={fetchResult.errors?.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setFetchResult(null)}>
            取得完了:
            {Object.entries(fetchResult.results || {}).map(([key, val]: [string, any]) => (
              <span key={key}> {key}={val.inserted ?? 0}件追加</span>
            ))}
            {fetchResult.errors?.length > 0 && ` | エラー: ${fetchResult.errors.join(', ')}`}
          </Alert>
        )}

        {/* コントロールパネル */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
          {/* 月クイック選択 */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            {Array.from({ length: 12 }, (_, i) => {
              const m = dayjs().subtract(i, 'month');
              const isSelected =
                startDate === m.startOf('month').format('YYYY-MM-DD') &&
                endDate === m.endOf('month').format('YYYY-MM-DD');
              return (
                <Button
                  key={i}
                  size="small"
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => {
                    setStartDate(m.startOf('month').format('YYYY-MM-DD'));
                    setEndDate(m.endOf('month').format('YYYY-MM-DD'));
                  }}
                  sx={{
                    fontSize: '0.7rem',
                    minWidth: 0,
                    px: 1,
                    py: 0.3,
                    ...(isSelected ? {} : { color: '#64748b', borderColor: '#e2e8f0' }),
                  }}
                >
                  {m.format('M')}月
                </Button>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              type="date"
              label="開始日"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              type="date"
              label="終了日"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>ステータス</InputLabel>
              <Select
                multiple
                value={statusFilter}
                onChange={handleStatusChange}
                label="ステータス"
                renderValue={(selected) => {
                  if ((selected as string[]).length === ALL_STATUSES.length) {
                    return <Chip label="全選択" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: STATUS_COLORS[s] || '#e2e8f0',
                            color: STATUS_COLORS[s] ? '#fff' : '#475569',
                          }}
                        />
                      ))}
                    </Box>
                  );
                }}
              >
                <MenuItem value="__ALL__">
                  <Checkbox checked={isAllStatusSelected} />
                  <ListItemText primary="全選択" />
                </MenuItem>
                {ALL_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    <Checkbox checked={statusFilter.includes(s)} />
                    <ListItemText
                      primary={
                        <Chip
                          label={s}
                          size="small"
                          sx={{
                            bgcolor: STATUS_COLORS[s] || '#e2e8f0',
                            color: STATUS_COLORS[s] ? '#fff' : '#475569',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="予約コード"
              value={contactCodeFilter}
              onChange={(e) => setContactCodeFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 140 }}
              placeholder="R0G7..."
            />
          </Box>
        </Paper>

        {/* 月別推移 + 当月サマリー */}
        <Paper sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
          <Box
            sx={{ p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
            onClick={() => toggleSection('monthly')}
          >
            <IconButton size="small" sx={{ mr: 1 }}>
              {sectionOpen.monthly ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>月別推移</Typography>
            {yearlyLoading && <CircularProgress size={20} />}
          </Box>
          <Collapse in={sectionOpen.monthly}>
          <Box sx={{ p: { xs: 2, md: 3 }, pt: 0 }}>
          {/* 当月ハイライト */}
          {(() => {
            const cur = yearlyData.find((r) => r.is_current);
            if (!cur) return null;
            return (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, py: 1, px: 1.5, bgcolor: '#f0f9ff', borderRadius: 1.5, border: '1px solid #bae6fd' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0369a1', mr: 1, display: 'flex', alignItems: 'center' }}>
                  {dayjs(cur.month + '-01').format('YYYY/M')}月
                </Typography>
                <Chip size="small" label={`決済 ¥${cur.payment.total.toLocaleString()}`} sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.7rem' }} />
                <Chip size="small" label={`売上 ¥${cur.sales.total_paid.toLocaleString()}`} sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: '0.7rem' }} />
                {cur.refunds.total > 0 && <Chip size="small" label={`返金 ¥${cur.refunds.total.toLocaleString()}`} sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600, fontSize: '0.7rem' }} />}
                <Chip size="small" label={`現金 ¥${cur.cash.total.toLocaleString()}`} sx={{ bgcolor: '#fef9c3', color: '#a16207', fontWeight: 600, fontSize: '0.7rem' }} />
                {cur.advance_carry?.total ? <Chip size="small" label={`前受繰越 ¥${cur.advance_carry.total.toLocaleString()}`} sx={{ bgcolor: '#f3e8ff', color: '#6d28d9', fontWeight: 600, fontSize: '0.7rem' }} /> : null}
              </Box>
            );
          })()}
          {yearlyData.length > 0 && (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', position: 'sticky', left: 0, zIndex: 3, minWidth: 80 }}>月</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', minWidth: 110 }}>
                      <HeaderWithHelp label="決済金額" tooltip="決済日が当月のカード決済合計（COMPLETED/APPROVED）" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', minWidth: 110 }}>
                      <HeaderWithHelp label="売上（実決済）" tooltip="貸出日が当月のInvoiceに紐づくPaymentの合計（返金前）" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', minWidth: 90 }}>
                      <HeaderWithHelp label="返金額" tooltip="当月に実行された返金の合計" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', minWidth: 90 }}>
                      <HeaderWithHelp label="現金売上" tooltip="レジドロワーの現金決済合計（カード決済を含まない）" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', minWidth: 90 }}>
                      <HeaderWithHelp label="前受金" tooltip="当月に決済されたが、貸出日が翌月以降のものの合計" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', minWidth: 100 }}>
                      <HeaderWithHelp label="前受金（繰越）" tooltip="月末時点の累計：決済済みだが貸出日が翌月以降のものの残高" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {yearlyData.map((row) => (
                    <TableRow
                      key={row.month}
                      sx={{
                        bgcolor: row.is_current ? '#eff6ff' : 'transparent',
                        '& td': row.is_current ? { fontWeight: 700 } : {},
                      }}
                    >
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          bgcolor: row.is_current ? '#dbeafe' : '#fff',
                          fontWeight: row.is_current ? 800 : 600,
                          fontSize: '0.85rem',
                          borderLeft: row.is_current ? '3px solid #3b82f6' : 'none',
                        }}
                      >
                        {dayjs(row.month + '-01').format('YYYY/M')}月
                        {row.is_current && (
                          <Chip label="当月" size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.6rem', bgcolor: '#3b82f6', color: '#fff' }} />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#15803d' }}>
                        {row.payment.total > 0 ? `¥${row.payment.total.toLocaleString()}` : '-'}
                        {row.payment.count > 0 && (
                          <Typography variant="caption" display="block" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                            {row.payment.count}件
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#1d4ed8' }}>
                        {row.sales.total_paid > 0 ? `¥${row.sales.total_paid.toLocaleString()}` : '-'}
                        {row.sales.count > 0 && (
                          <Typography variant="caption" display="block" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                            {row.sales.count}件
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', color: row.refunds.total > 0 ? '#dc2626' : '#94a3b8' }}>
                        {row.refunds.total > 0 ? `¥${row.refunds.total.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#a16207' }}>
                        {row.cash.total > 0 ? `¥${row.cash.total.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', color: row.advance?.total > 0 ? '#7c3aed' : '#94a3b8' }}>
                        {row.advance?.total > 0 ? `¥${row.advance.total.toLocaleString()}` : '-'}
                        {row.advance?.count > 0 && (
                          <Typography variant="caption" display="block" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                            {row.advance.count}件
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', color: row.advance_carry?.total > 0 ? '#6d28d9' : '#94a3b8', fontWeight: 600 }}>
                        {row.advance_carry?.total > 0 ? `¥${row.advance_carry.total.toLocaleString()}` : '-'}
                        {row.advance_carry?.count > 0 && (
                          <Typography variant="caption" display="block" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                            {row.advance_carry.count}件
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </Box>
          </Collapse>
        </Paper>

        {/* データテーブル */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
          <Box
            sx={{ p: 2, borderBottom: sectionOpen.payments ? '1px solid #e2e8f0' : 'none', display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
            onClick={() => toggleSection('payments')}
          >
            <IconButton size="small" sx={{ mr: 1 }}>
              {sectionOpen.payments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
              決済一覧 ({total}件)
            </Typography>
          </Box>
          <Collapse in={sectionOpen.payments}>

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : records.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: '#94a3b8' }}>
              <Typography>データがありません。「データを取得」でSquareからデータを取得してください。</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>決済日</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>貸出日</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>予約コード</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>顧客名</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>金額</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>支払済</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>ステータス</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>決済方法</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((rec) => (
                    <React.Fragment key={rec.id}>
                      <TableRow
                        hover
                        onClick={() => setExpandedRow(expandedRow === rec.id ? null : rec.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ width: 32, px: 0.5 }}>
                          <IconButton size="small">
                            {expandedRow === rec.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {rec.payment_created_at ? dayjs(rec.payment_created_at).format('MM/DD HH:mm') : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {rec.sale_or_service_date ? dayjs(rec.sale_or_service_date).format('MM/DD') : '-'}
                        </TableCell>
                        <TableCell>
                          {rec.contact_code ? (
                            <Chip label={rec.contact_code} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {[rec.customer_family_name, rec.customer_given_name].filter(Boolean).join(' ') || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          {formatYen(rec.total_amount)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.85rem', color: rec.total_completed_amount ? '#10b981' : '#94a3b8' }}>
                          {formatYen(rec.total_completed_amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={rec.payment_status || '-'}
                            size="small"
                            sx={{
                              bgcolor: STATUS_COLORS[rec.payment_status || ''] || '#e2e8f0',
                              color: STATUS_COLORS[rec.payment_status || ''] ? '#fff' : '#475569',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {rec.entry_method && ['CHIP', 'CONTACTLESS', 'SWIPED'].includes(rec.entry_method) ? (
                            <Chip icon={<StoreIcon />} label={rec.entry_method} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          ) : (
                            <Chip icon={<CreditCardIcon />} label={rec.entry_method || 'オンライン'} size="small" color="info" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          )}
                        </TableCell>
                      </TableRow>

                      {/* 展開行: 明細 */}
                      <TableRow>
                        <TableCell colSpan={9} sx={{ p: 0, border: expandedRow === rec.id ? undefined : 'none' }}>
                          <Collapse in={expandedRow === rec.id}>
                            <Box sx={{ p: 2, bgcolor: '#fafbfc' }}>
                              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Payment ID</Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{rec.payment_id}</Typography>
                                </Box>
                                {rec.order_id && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Order ID</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{rec.order_id}</Typography>
                                  </Box>
                                )}
                                {rec.invoice_id && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Invoice ID</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{rec.invoice_id}</Typography>
                                  </Box>
                                )}
                                {rec.customer_email && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">メール</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{rec.customer_email}</Typography>
                                  </Box>
                                )}
                                {rec.invoice_title && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">タイトル</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{rec.invoice_title}</Typography>
                                  </Box>
                                )}
                                {rec.card_brand && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">カード</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{rec.card_brand} ****{rec.card_last4}</Typography>
                                  </Box>
                                )}
                                {rec.receipt_url && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">レシート</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                      <a href={rec.receipt_url} target="_blank" rel="noopener noreferrer">表示</a>
                                    </Typography>
                                  </Box>
                                )}
                                {(rec.refunded_amount ?? 0) > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">返金額</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#ef4444', fontWeight: 600 }}>
                                      {formatYen(rec.refunded_amount)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              {rec.line_items && rec.line_items.length > 0 && (
                                <LineItemsTable items={rec.line_items} />
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </Collapse>
        </Paper>

        {/* ドロワー入出金ログ */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
          <Box
            sx={{ p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
            onClick={() => toggleSection('cash')}
          >
            <IconButton size="small" sx={{ mr: 1 }}>
              {sectionOpen.cash ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
              入出金ログ
              {cashSummary && ` (${cashSummary.event_count}件 / ${cashSummary.shift_count}シフト)`}
            </Typography>
            {cashLoading && <CircularProgress size={20} />}
          </Box>
          <Collapse in={sectionOpen.cash}>
          {cashSummary && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', p: 2, bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0' }}>
              <Chip size="small" label={`入金 ¥${cashSummary.total_paid_in.toLocaleString()}`} sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.7rem' }} />
              <Chip size="small" label={`出金 ¥${Math.abs(cashSummary.total_paid_out).toLocaleString()}`} sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 600, fontSize: '0.7rem' }} />
              <Chip size="small" label={`現金売上 ¥${cashSummary.total_sales.toLocaleString()}`} sx={{ bgcolor: '#fef9c3', color: '#a16207', fontWeight: 600, fontSize: '0.7rem' }} />
              {cashSummary.total_refunds !== 0 && (
                <Chip size="small" label={`返金 ¥${Math.abs(cashSummary.total_refunds).toLocaleString()}`} sx={{ bgcolor: '#fce7f3', color: '#be185d', fontWeight: 600, fontSize: '0.7rem' }} />
              )}
            </Box>
          )}
          {cashEvents.length > 0 ? (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>日時</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>種類</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>金額</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>メモ</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>端末</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cashEvents.map((ev, idx) => {
                    const typeLabels: Record<string, { label: string; color: string }> = {
                      PAID_IN: { label: '入金', color: '#10b981' },
                      PAID_OUT: { label: '出金', color: '#ef4444' },
                      CASH_TENDER_PAYMENT: { label: '現金売上', color: '#f59e0b' },
                      CASH_TENDER_REFUND: { label: '現金返金', color: '#ec4899' },
                      CASH_TENDER_CANCELLED_PAYMENT: { label: '取消', color: '#94a3b8' },
                    };
                    const tInfo = typeLabels[ev.type] || { label: ev.type, color: '#94a3b8' };
                    const isNegative = ['PAID_OUT', 'CASH_TENDER_REFUND'].includes(ev.type);
                    return (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {ev.created_at ? dayjs(ev.created_at).format('MM/DD HH:mm') : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip label={tInfo.label} size="small" sx={{ bgcolor: tInfo.color, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem', color: isNegative ? '#ef4444' : '#10b981' }}>
                          {isNegative ? '-' : ''}¥{Math.abs(ev.amount).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ev.description || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          {ev.device_name || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : !cashLoading ? (
            <Box sx={{ p: 4, textAlign: 'center', color: '#94a3b8' }}>
              <Typography>期間内のドロワーイベントがありません</Typography>
            </Box>
          ) : null}
          </Collapse>
        </Paper>
      </Box>

      {/* データ取得モーダル */}
      <Dialog open={fetchModalOpen} onClose={() => !fetching && setFetchModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Squareからデータを取得</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* 月単位クイック選択 */}
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', mb: 0.5, display: 'block' }}>月単位で選択</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {Array.from({ length: 6 }, (_, i) => {
                  const m = dayjs().subtract(i, 'month');
                  return (
                    <Button
                      key={i}
                      size="small"
                      variant={
                        fetchStartDate === m.startOf('month').format('YYYY-MM-DD') &&
                        fetchEndDate === m.endOf('month').format('YYYY-MM-DD')
                          ? 'contained' : 'outlined'
                      }
                      onClick={() => {
                        setFetchStartDate(m.startOf('month').format('YYYY-MM-DD'));
                        setFetchEndDate(m.endOf('month').format('YYYY-MM-DD'));
                      }}
                      sx={{ fontSize: '0.75rem', minWidth: 0, px: 1.5 }}
                    >
                      {m.format('YYYY/M')}月
                    </Button>
                  );
                })}
              </Box>
            </Box>

            <TextField
              type="date"
              label="取得開始日"
              value={fetchStartDate}
              onChange={(e) => setFetchStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              type="date"
              label="取得終了日"
              value={fetchEndDate}
              onChange={(e) => setFetchEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={locationIndex}
                onChange={(e) => setLocationIndex(Number(e.target.value))}
                label="Location"
              >
                <MenuItem value={0}>Location 0</MenuItem>
                <MenuItem value={1}>Location 1</MenuItem>
              </Select>
            </FormControl>
            {/* 取得対象API チェックボックス */}
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, mb: 0.5, display: 'block' }}>取得対象</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                {[
                  { key: 'payments', label: 'Payments' },
                  { key: 'orders', label: 'Orders' },
                  { key: 'invoices', label: 'Invoices' },
                  { key: 'refunds', label: 'Refunds' },
                  { key: 'cash_drawers', label: 'Cash Drawers' },
                  { key: 'customers', label: 'Customers（全同期）' },
                ].map(({ key, label }) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={fetchApis[key]} onChange={() => toggleFetchApi(key)} size="small" sx={{ p: 0.5 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#475569' }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            {fetchApis.invoices && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                <Checkbox
                  checked={resyncInvoices}
                  onChange={(e) => setResyncInvoices(e.target.checked)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#475569' }}>
                  Invoice全再同期（取消・ステータス変更を反映）
                </Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              ※ 期間が長いとタイムアウトする場合があります。1ヶ月単位での取得を推奨します。
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFetchModalOpen(false)} disabled={fetching}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleFetchFromSquare}
            disabled={fetching}
            startIcon={fetching ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
            sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
          >
            {fetching ? '取得中...' : '取得開始'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
